import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LeadModalProvider } from "@/context/LeadModalContext";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { MobileCTA } from "@/components/layout/MobileCTA";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import AdminContentPage from "@/pages/AdminContentPage";
import TutoringPage from "@/pages/TutoringPage";
import QuranicStudiesPage from "@/pages/QuranicStudiesPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import BlogsPage from "@/pages/BlogsPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AdminBlogsPage from "@/pages/AdminBlogsPage";
import AboutPage from "@/pages/AboutPage";
import LanguageCoursePage from "@/pages/LanguageCoursePage";
import LanguageCourseDetailPage from "@/pages/LanguageCourseDetailPage";
import TutorsPage from "@/pages/TutorsPage";
import AdminEnrollmentsPage from "@/pages/AdminEnrollmentsPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentResultPage";
import { CATEGORIES } from "@/data/categories";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LeadModalProvider>
            <ScrollToTop />
            <Navbar />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/admin/leads" element={<AdminLeadsPage />} />
              <Route path="/admin/content" element={<AdminContentPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/services/online-tutoring" element={<TutoringPage />} />
              <Route path="/ielts-courses" element={<LanguageCoursePage program="ielts" />} />
              <Route path="/toefl-courses" element={<LanguageCoursePage program="toefl" />} />
              <Route path="/english-language-courses" element={<LanguageCoursePage program="english-language" />} />
              <Route path="/english-literature-courses" element={<LanguageCoursePage program="english-literature" />} />
              <Route path="/ai-digital-literature-courses" element={<LanguageCoursePage program="ai-digital-literature" />} />
              <Route path="/tutors" element={<TutorsPage />} />
              <Route path="/language-course/:courseId" element={<LanguageCourseDetailPage />} />
              <Route path="/quranic-studies" element={<QuranicStudiesPage />} />
              <Route path="/quranic-studies/:courseId" element={<CourseDetailPage />} />
              <Route path="/blogs" element={<BlogsPage />} />
              <Route path="/blogs/:slug" element={<BlogPostPage />} />
              <Route path="/admin/blogs" element={<AdminBlogsPage />} />
              <Route path="/admin/enrollments" element={<AdminEnrollmentsPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              {CATEGORIES.filter((c) => !["online-tutoring", "quranic-studies", "blogs", "ielts-courses", "toefl-courses", "english-language-courses", "english-literature-courses", "ai-digital-literature-courses"].includes(c.slug)).map((c) => (
                <Route key={c.slug} path={c.path} element={<ComingSoonPage slug={c.slug} />} />
              ))}
              <Route path="/privacy-policy" element={<ComingSoonPage slug="privacy-policy" />} />
              <Route path="/terms-of-service" element={<ComingSoonPage slug="terms-of-service" />} />
              <Route path="*" element={<ComingSoonPage slug="404" />} />
            </Routes>
            <Footer />
            <MobileCTA />
            <Toaster richColors position="top-center" />
          </LeadModalProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
